import React, { useCallback, useEffect, useMemo, useState } from 'react';
import MainScreenHeader from '../../common/Header/MainScreenHeader';
import DetailedListComponent, { DetailedListColumn } from '../../common/DetailedList/DetailedListComponent';
import ErrorMessage from '../../common/ErrorMessage/ErrorMessage';
import RoleGuard from '../../common/RoleGuard';
import { UserRoles } from '../../../constants/UserRoles';
import { SupplierRestDataProvider } from '../../../data/SupplierRestDataProvider';
import { Supplier } from '../../../models/Supplier';
import SupplierDialog from './SupplierDialog';
import './Suppliers.scss';
import '../../../styles/GenericSiteStyles.scss';
import { useTranslation } from 'react-i18next';

const Suppliers: React.FC = () => {
  const { t } = useTranslation(['common', 'screens']);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);

  const provider = useMemo(() => new SupplierRestDataProvider(), []);

  const loadSuppliers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setSuppliers(await provider.getAll());
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [provider]);

  useEffect(() => { loadSuppliers(); }, [loadSuppliers]);

  const columns: DetailedListColumn<Supplier>[] = useMemo(() => [
    {
      key: 'name',
      header: t('screens:suppliers.columns.name'),
      dataField: 'name',
      type: 'string',
    },
    {
      key: 'isCompany',
      header: t('common:type'),
      dataField: 'isCompany',
      width: 110,
      fixedWidth: true,
      render: s => s.isCompany
        ? <span className="badge bg-primary">{t('screens:suppliers.badges.company')}</span>
        : <span className="badge bg-secondary">{t('screens:suppliers.badges.individual')}</span>,
    },
    {
      key: 'emailAddress',
      header: t('screens:suppliers.columns.email'),
      dataField: 'emailAddress',
      type: 'string',
    },
    {
      key: 'phoneNumber',
      header: t('screens:suppliers.columns.phone'),
      dataField: 'phoneNumber',
      type: 'string',
      width: 150,
      fixedWidth: true,
    },
    {
      key: 'country',
      header: t('screens:suppliers.columns.country'),
      dataField: 'country',
      type: 'string',
      width: 130,
      fixedWidth: true,
    },
  ], [t]);

  const openEdit = (supplier: Supplier) => {
    setSelectedSupplier(supplier);
    setDialogOpen(true);
  };

  const openAdd = () => {
    setSelectedSupplier(null);
    setDialogOpen(true);
  };

  const closeDialog = () => setDialogOpen(false);

  const handleSave = async (supplier: Supplier): Promise<Supplier> => {
    try {
      if (!supplier.id) {
        const saved = { ...supplier, id: crypto.randomUUID() };
        await provider.add(saved);
        await loadSuppliers();
        return saved;
      } else {
        await provider.update(supplier.id, supplier);
        await loadSuppliers();
        return supplier;
      }
    } catch (e: any) {
      setError(e.message);
      throw e;
    }
  };

  const handleDelete = async (supplier: Supplier) => {
    try {
      await provider.delete(supplier.id);
      closeDialog();
      await loadSuppliers();
    } catch (e: any) {
      setError(e.message);
    }
  };

  return (
    <RoleGuard roles={`${UserRoles.Admin},${UserRoles.Manager},${UserRoles.User}`} redirectOnDeny>
      <div className="suppliers-page">
        <MainScreenHeader title={t('screens:suppliers.title')} />
        {error && <ErrorMessage message={error} />}
        <DetailedListComponent
          dataSource={{ data: suppliers, setData: setSuppliers }}
          columns={columns}
          rowKey={s => s.id}
          editRole={`${UserRoles.Admin},${UserRoles.Manager}`}
          addRole={`${UserRoles.Admin},${UserRoles.Manager}`}
          onEditRequest={openEdit}
          onAddRequest={openAdd}
        />
        {loading && <div className="suppliers-loading">{t('common:loading')}</div>}
        <SupplierDialog
          open={dialogOpen}
          supplier={selectedSupplier}
          onSave={handleSave}
          onDelete={handleDelete}
          onClose={closeDialog}
        />
      </div>
    </RoleGuard>
  );
};

export default Suppliers;
